import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Shield, Trash2, Check, X, AlertTriangle, Copy, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API_URL = window.location.hostname.includes('preview.emergentagent.com')
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

export default function AdminDashboard() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | pending | approved | disapproved
  const [actionLoading, setActionLoading] = useState(null);
  const [disapproveTarget, setDisapproveTarget] = useState(null);
  const navigate = useNavigate();
  const { currentColors } = useTheme();
  const [c1, c2, c3] = currentColors || ['#FFD6E0', '#E0D4FF', '#C8F4D6'];

  const fetchQuestions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/wyr`, { withCredentials: true });
      setQuestions(res.data.questions);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const handleDelete = async (id) => {
    setActionLoading(id);
    try {
      await axios.delete(`${API_URL}/api/admin/wyr/${id}`, { withCredentials: true });
      setQuestions(prev => prev.filter(q => q.id !== id));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setActionLoading(null);
    }
  };

  const handleApprove = async (id) => {
    setActionLoading(id);
    try {
      await axios.post(`${API_URL}/api/admin/wyr/${id}/approve`, {}, { withCredentials: true });
      setQuestions(prev => prev.map(q => q.id === id ? { ...q, status: 'approved' } : q));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDisapprove = async (id, reason) => {
    setActionLoading(id);
    try {
      await axios.post(`${API_URL}/api/admin/wyr/${id}/disapprove`, { reason }, { withCredentials: true });
      setQuestions(prev => prev.filter(q => q.id !== id));
      setDisapproveTarget(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to disapprove');
    } finally {
      setActionLoading(null);
    }
  };

  const handleLogout = () => {
    document.cookie = 'admin_token=; Max-Age=0; path=/';
    navigate('/login');
  };

  const filtered = questions.filter(q => {
    if (filter === 'all') return true;
    if (filter === 'pending') return q.status === 'pending' && q.type === 'user';
    return true;
  });
  const counts = {
    all: questions.length,
    pending: questions.filter(q => q.status === 'pending' && q.type === 'user').length,
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--page-bg)' }}>
      {/* Header */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b-[1.5px] border-[var(--border-soft)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-[#1A1A1A]" />
            <h1 className="text-lg font-heading font-bold text-[#1A1A1A]">Admin Panel</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleLogout}
              className="px-4 py-2 rounded-full border-[1.5px] border-[var(--border-soft)] shadow-sm bg-white hover:bg-[var(--c1)] text-[#1A1A1A] font-heading font-bold text-sm transition-all"
              data-testid="admin-logout-btn"
            >
              <ArrowLeft className="w-4 h-4 inline mr-1" /> Exit Admin
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Title */}
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-[#1A1A1A] mb-2">WYR Question Moderation</h2>
          <p className="text-[#6B7280] font-medium">Review, approve, or remove user-submitted questions</p>
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8" data-testid="admin-filter-tabs">
          {[
            { key: 'all', label: 'All Questions', color: c1 },
            { key: 'pending', label: 'Pending', color: '#FDE68A' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-4 py-2 rounded-full text-sm font-bold border-[1.5px] transition-all ${
                filter === tab.key
                  ? 'border-[#1A1A1A] shadow-md scale-105'
                  : 'border-[var(--border-soft)] hover:border-[var(--c2)]'
              }`}
              style={{ backgroundColor: filter === tab.key ? tab.color : 'white' }}
              data-testid={`filter-${tab.key}`}
            >
              {tab.label} ({counts[tab.key]})
            </button>
          ))}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap justify-center gap-4 mb-5 text-xs text-[#6B7280]">
          <span className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> Approve</span>
          <span className="flex items-center gap-1"><X className="w-3 h-3 text-red-400" /> Disapprove</span>
          <span className="flex items-center gap-1"><Trash2 className="w-3 h-3 text-[#9CA3AF]" /> Delete</span>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-3 border-[var(--c1)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-[#9CA3AF] font-medium">No questions to show</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((q) => (
              <div
                key={q.id}
                className={`rounded-xl border-[1.5px] bg-white px-4 py-3 transition-all ${
                  q.status === 'approved' ? 'border-emerald-200' : 'border-[var(--border-soft)]'
                }`}
                data-testid={`admin-wyr-card-${q.id}`}
              >
                <div className="flex items-center gap-3">
                  {/* Badges */}
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      q.type === 'ai' ? 'bg-violet-100 text-violet-700' : 'bg-sky-100 text-sky-700'
                    }`}>
                      {q.type === 'ai' ? 'AI' : 'User'}
                    </span>
                    {q.type === 'user' && (
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        q.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                      }`} data-testid={`status-badge-${q.id}`}>
                        {q.status}
                      </span>
                    )}
                  </div>

                  {/* Options inline */}
                  <div className="flex-1 min-w-0 flex items-center gap-2 text-sm">
                    <span className="font-medium text-[#1A1A1A] truncate">{q.option_a}</span>
                    <span className="text-[#9CA3AF] flex-shrink-0">vs</span>
                    <span className="font-medium text-[#1A1A1A] truncate">{q.option_b}</span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {q.type === 'user' && q.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleApprove(q.id)}
                          disabled={actionLoading === q.id}
                          className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-all disabled:opacity-40"
                          title="Approve"
                          data-testid={`approve-btn-${q.id}`}
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDisapproveTarget(disapproveTarget === q.id ? null : q.id)}
                          disabled={actionLoading === q.id}
                          className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-all disabled:opacity-40"
                          title="Disapprove"
                          data-testid={`disapprove-btn-${q.id}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleDelete(q.id)}
                      disabled={actionLoading === q.id}
                      className="p-1.5 rounded-lg text-[#C0C0C0] hover:text-red-500 hover:bg-red-50 transition-all disabled:opacity-40"
                      title="Delete"
                      data-testid={`delete-btn-${q.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Disapprove reasons (expandable) */}
                {disapproveTarget === q.id && (
                  <div className="mt-2 flex gap-2 pl-14" data-testid={`disapprove-reasons-${q.id}`}>
                    <button
                      onClick={() => handleDisapprove(q.id, 1)}
                      disabled={actionLoading === q.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-700 transition-all disabled:opacity-40"
                      data-testid={`reason-similar-${q.id}`}
                    >
                      <Copy className="w-3 h-3" /> Similar (+10pts)
                    </button>
                    <button
                      onClick={() => handleDisapprove(q.id, 2)}
                      disabled={actionLoading === q.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 transition-all disabled:opacity-40"
                      data-testid={`reason-inappropriate-${q.id}`}
                    >
                      <AlertTriangle className="w-3 h-3" /> Offensive (-20pts)
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
